import React, { useEffect, useState } from 'react';
import type { PurchaseItem } from '../types';
import { getRecentlySold } from '../services/tracking';

const STORAGE_KEY = 'onejog_popup_closed';
const POPUP_DELAY_MS = 3000;

interface PopupRecentlySoldProps {
  siteId: string;
  maxItems?: number;
  onClose?: () => void;
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 2147483646,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    boxSizing: 'border-box',
    pointerEvents: 'auto',
  },
  box: {
    backgroundColor: '#fff',
    borderRadius: 12,
    maxWidth: 420,
    width: '100%',
    maxHeight: '85vh',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    padding: '16px 20px',
    borderBottom: '1px solid #eee',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    margin: 0,
    fontSize: 18,
    fontWeight: 600,
    color: '#1a1a1a',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: 24,
    cursor: 'pointer',
    color: '#666',
    lineHeight: 1,
    padding: 4,
  },
  list: {
    padding: 12,
    overflowY: 'auto',
    maxHeight: 360,
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 8px',
    borderRadius: 8,
    textDecoration: 'none',
    color: '#1a1a1a',
    marginBottom: 4,
  },
  img: {
    width: 56,
    height: 56,
    objectFit: 'cover',
    borderRadius: 8,
    backgroundColor: '#f0f0f0',
  },
  itemInfo: {
    flex: 1,
    minWidth: 0,
  },
  itemName: {
    fontWeight: 500,
    fontSize: 14,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  itemPrice: {
    fontSize: 13,
    color: '#22c55e',
    fontWeight: 600,
    marginTop: 2,
  },
};

export function PopupRecentlySold({ siteId, maxItems = 5, onClose }: PopupRecentlySoldProps) {
  const [items, setItems] = useState<PurchaseItem[]>([]);
  const [visible, setVisible] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    getRecentlySold(siteId, maxItems).then((data) => {
      setItems(data);
      setLoaded(true);
    });
  }, [siteId, maxItems]);

  useEffect(() => {
    if (!loaded || items.length === 0) return;
    const closed = sessionStorage.getItem(STORAGE_KEY);
    if (closed) return;
    const t = setTimeout(() => setVisible(true), POPUP_DELAY_MS);
    return () => clearTimeout(t);
  }, [loaded, items.length]);

  const close = () => {
    sessionStorage.setItem(STORAGE_KEY, '1');
    setVisible(false);
    onClose?.();
  };

  if (!visible || items.length === 0) return null;

  return (
    <div role="dialog" aria-label="Productos recién vendidos" style={styles.overlay} onClick={close}>
      <div style={styles.box} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Otros clientes acaban de comprar</h2>
          <button type="button" style={styles.closeBtn} onClick={close} aria-label="Cerrar">
            ×
          </button>
        </div>
        <div style={styles.list}>
          {items.map((item) => (
            <a
              key={item.item_id}
              href={item.product_url || '#'}
              style={styles.item}
              target="_blank"
              rel="noopener noreferrer"
            >
              {item.image_url ? (
                <img src={item.image_url} alt="" style={styles.img} loading="lazy" />
              ) : (
                <div style={styles.img} />
              )}
              <div style={styles.itemInfo}>
                <div style={styles.itemName}>{item.item_name}</div>
                <div style={styles.itemPrice}>
                  {item.currency || ''} ${item.price.toFixed(2)}
                  {item.quantity > 1 ? ` × ${item.quantity}` : ''}
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
